/*  Copyright (C) 2025, 2026  Paul B. Lucas   Licensed to University of Illinois   */
//
//  This is the authentication composable for the EquipmentLoanAgreementManager Vue3 Web-app
//  hosted on a F&S tenant.  This is used in conjunction with configuration/msalConfig.ts
//
//  Description:
//      This package implements OAuth2.0 'authorization code flow with PKCE' using a redirect auth method
//      Redirects are required since SSO authentication is provided by a common University Login page (with 2FA)
//      Admin groups are obtained from the claims token.  These are set on the RWA app registration via the Enterprise 
//      Application tab from the App Registration Overview.  Only the configured groups are returned on the claim.
//      In addition, several AD attributes such as UIN are returned as optional claims. (These need to be added by an
//      IAMU administrator). These optional claims include UIN which is used as a key for Banner requests.
//
//  WARNING:
//      This code is highly asynchronopus and extensively uses Promises.  It is inherently re-entrant since authentication
//      is via URL redirect.  Failure to abide by timing considerations through omission of 'awaits' or 'thenables' WILL result in
//      errors in the auth process.  Further this code works in conjunction with Entra App registrations and both must be
//      considered with developing or changing this package.
//
//  Authentication Flow:
//      Typically a user will have already signed into their machine/university network and a cached token will be
//      present. This however does not guarantee that this application has obtained user permissions.  If such a permission
//      token is NOT present, a redirect will still occur to present the user with a dialogue to obtain these permissions:
//      e.g."This app wants to user your data."   User must click thru.
//
//      The process flow then redirects to the campus SSO login page with 2FA, either for ab initio authentication 
//      (likely never will happen given typical user behavior) or for the permissions dialogue.
//      Flow steps:
//          1.  App.vue calls handleAuthentication() during setup()
//          2.  MSAL-browser is initialized and the myMSALObj object is created.  Since this requires a request to Entra,
//              it is asynchronous and must be awaited.  Failure to await will will return an 'object not initialized' error.
//          3.  handleRedirectPromise() is asynchronpously called.  Completion must be awaited for Promise fulfillment. 
//              This function returns a redirectResponse object || null.  If not null, then user data is available (likely
//              from a credential/token cache).
//          4a. If response is non-null, the graphAccessToken is acquired as the 'accessToken' property on the response.
//              The list of accounts is obtained from the myMSALObject (this is updated in the fulfillment of the Promise).
//              If the array has members (length > 0), then the user state is set to [0] element (current account).
//              If the account list is empty, an error should be thrown, but is currently ignored.
//          4b. If the response is null, then no user token exists in the cache, and we redirect to the campus login page
//              for authentications and/or permissions.  This is done by calling loginRedirect() method on the myMSALObj. 
//              NOTE: Code execution STOPS here - the browser is redirected to the campus login page and all the Vue page code
//              is flushed. 
//          5.  The redirect return upon authentication will cause the page to be reloaded, and the handleAuthentication()
//              function to be re-entered.  At this point the handleRedirectPromise() will have a non-null response and
//              the user state and groups claims will be present.
//          6.  Return to setup() function in App.vue.
//
//  Changes:
//
//      02/25/2025      pblucas     Created
//      03/24/2026      pblucas     Reviewed
//


import { ref } from 'vue' 
import { myMSALObj, authState } from '@/configuration/msalConfig' 

const useAuthAPI = () => {

    const graphAccessToken  =   ref()
    const expireDate        =   ref()
         
    // async wrapper
    const handleAuthentication = async () =>  {
        // test
        await myMSALObj.initialize()
        //console.log("App:: setup: MSAL initialization complete")

        const redirectResponse  = await myMSALObj.handleRedirectPromise()

        if(redirectResponse !== null) {
            graphAccessToken.value  =   redirectResponse.accessToken
            expireDate.value        =   redirectResponse.expiresOn

            // debug
            //console.log("useAuthAPI:: token expiresOn: "+expireDate.value.toString())

            const accounts = myMSALObj.getAllAccounts()
            if(accounts.length > 0) {
                myMSALObj.setActiveAccount(accounts[0]) 
                authState.user = accounts[0]  
            }
            else {
                console.log("SysEerror: useAuthAPI:: handleAuthentication: No accounts on non-null promise response")
            }
        }
        else  {
            // redirectResponse is null - no cached user - need to login
            // redirect to login page
            await myMSALObj.loginRedirect()
            // nothing after here is executable    
        }
    }   
    
    const handleLogout = async () => {
        await myMSALObj.logoutRedirect( {
            postLogoutRedirectUri: "/"
        })
    }

    const refreshAccessToken = async () =>  {
        
        const silentTokenRequest = {
            account: myMSALObj.getAllAccounts()[0],
            scopes: ["User.Read", "GroupMember.Read.All", "GroupMember.ReadWrite.All"],
        };
        await myMSALObj.acquireTokenSilent(silentTokenRequest)
        .then((response:any) => {
            
            graphAccessToken.value  =   response.accessToken
            expireDate.value        =   response.expiresOn
            //console.log("useAuthAPI:: refreshAccessToken: gat: "+JSON.stringify(graphAccessToken.value))
        })
        .catch((error: any) => {
            console.log("useAuthAPI:: refreshAccessToken: could not refresh access token")
            myMSALObj.acquireTokenRedirect(silentTokenRequest)
        });   
    }
    
    const checkAccessToken = async() =>     {
        let currentDate = new Date()
        // compare on milliseconds since epoch
        if ( currentDate.getTime() > expireDate.value.getTime()) {
            //console.log("useAuthAPI:: checkAccessToken: access token expired, refreshing ...")
            refreshAccessToken()
        }
        else {
          //console.log("useAuthAPI:: checkAccessToken: access token valid")  
        }
    }
    
    
    return {handleAuthentication, handleLogout, refreshAccessToken, checkAccessToken, 
            authState, expireDate, graphAccessToken}
}
export default useAuthAPI